<script setup lang="ts">
import { ref, computed, Ref, nextTick } from 'vue';
import { AddressBookItem, useAddressBookStore } from '@/store/addressBook';
import { BUNDLE_MAX_SIZE } from '@/constants';
import BaseAddress from '@/components/molecules/BaseAddress.vue';
import { shortAddress } from '@/helpers/shortAddress';
import { useRouter } from 'vue-router';
import BaseAddressSystemIcon from '@/components/BaseAddressSystemIcon.vue';
import { snackbarPush } from '@/helpers/snackbarPush';
import { Sortable } from 'sortablejs-vue3';
const store = useAddressBookStore();
const router = useRouter();

const activeInputAddress: Ref<null | string> = ref(null);
const props = defineProps<{
  mode: 'items' | 'editBundle';
}>();

const input = ref('');
const isLoading = ref(false);
const items = computed(() => {
  switch (props.mode) {
    case 'items':
      return store.items;
    case 'editBundle':
      return store.editBundle.items;
    default:
      return [];
  }
});

async function onItemEdit(address: string) {
  activeInputAddress.value = address;
  await nextTick();
  document
    .getElementsByName(`input-item-${props.mode}-${address}`)?.[0]
    .focus();
}

function onItemEditEnter() {
  (document.activeElement as HTMLElement)?.blur();
}

async function unfocusedItemEdit(address: string) {
  let item;
  switch (props.mode) {
    case 'items':
      item = store.getItem(address);
      break;
    case 'editBundle':
      item = store.getEditBundleItem(address);
      break;
    default:
      break;
  }
  if (!item) return;

  if (item.name === '' || item.name === undefined)
    item.name = shortAddress(item.address);

  activeInputAddress.value = null;
}

async function addAddress() {
  isLoading.value = true;
  let promise: Promise<void> | undefined;
  switch (props.mode) {
    case 'items':
      promise = store.addAddress(input.value);
      break;
    case 'editBundle':
      promise = store.addAddressToEditBundle(input.value);
      break;
    default:
      break;
  }
  await promise;
  isLoading.value = false;
  input.value = '';
}

function removeAddress(address: string) {
  switch (props.mode) {
    case 'items':
      store.removeAddress(address);
      break;
    case 'editBundle':
      store.removeAddressFromEditBundle(address);
      break;
    default:
      break;
  }
}

function share(item: AddressBookItem) {
  const { href } = router.resolve({
    name: 'Portfolio',
    params: { addresses: item.address },
  });
  if (navigator.share) {
    navigator
      .share({
        title: `Jupiter Portfolio | ${shortAddress(item.address)}`,
        url: href,
      })
      .catch(() => {});
  } else {
    navigator.clipboard.writeText(`${window.location.origin}${href}`);
    snackbarPush('Copied to clipboard.');
  }
}

function pin(item: AddressBookItem) {
  store.pinAddress(item.address, !item.pinned);
}

const onEnd = (event: { oldIndex: number; newIndex: number }) => {
  switch (props.mode) {
    case 'items':
      store.moveItem(event.oldIndex, event.newIndex);
      break;
    case 'editBundle':
      store.moveAddressFromEditBundle(event.oldIndex, event.newIndex);
      break;
    default:
      break;
  }
};

const options = computed(() => ({
  direction: 'vertical',
  handle: '.handler',
}));
</script>

<template>
  <div>
    <div class="d-flex align-center">
      <v-text-field
        style="max-width: 500px"
        v-model="input"
        hide-details
        variant="solo-filled"
        density="compact"
        class="mr-2"
        label="Address or domain"
        single-line
        @keyup.enter="addAddress"
      ></v-text-field>
      <v-btn @click="addAddress" :loading="isLoading" height="40px">Add</v-btn>
    </div>
    <v-card class="my-4" v-if="items.length > 0">
      <v-list class="py-0 address-table">
        <Sortable
          :list="items"
          item-key="address"
          @end="onEnd"
          :options="options"
        >
          <template #item="{ element, index }">
            <v-list-item
              v-bind="props"
              class="draggable"
              :key="element.address"
            >
              <template #prepend>
                <div class="handler me-2"><v-icon icon="mdi-drag" /></div>

                <!-- @disabled-jup -->
                <!-- <base-address-system-icon
                  :address="element.address"
                ></base-address-system-icon> -->
              </template>
              <v-list-item-title>
                <div
                  v-if="activeInputAddress === element.address"
                  class="d-flex align-center"
                >
                  <input
                    v-model="element.name"
                    type="text"
                    class="ma-1"
                    @keyup.enter="onItemEditEnter"
                    @focusout="unfocusedItemEdit(element.address)"
                    :name="`input-item-${mode}-${element.address}`"
                  />
                  <v-btn size="small" min-width="0" class="px-2">
                    <v-icon icon="mdi-check"></v-icon>
                  </v-btn>
                </div>
                <div v-else class="d-flex align-center">
                  <div>
                    {{ element.name }}
                  </div>
                  <v-btn
                    size="small"
                    min-width="0"
                    class="px-2"
                    @click="onItemEdit(element.address)"
                    variant="plain"
                  >
                    <v-icon icon="mdi-pencil"></v-icon>
                  </v-btn>
                </div>
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption">
                <base-address
                  :address="element.address"
                  click-to-copy
                ></base-address>
              </v-list-item-subtitle>
              <template #append>
                <v-btn
                  v-if="props.mode === 'editBundle'"
                  @click="removeAddress(element.address)"
                  size="small"
                  variant="plain"
                  icon="mdi-delete"
                />
                <v-menu
                  v-else
                  location="bottom right"
                  offset="0 -10"
                  :transition="false"
                >
                  <template v-slot:activator="{ props }">
                    <v-btn
                      size="small"
                      variant="plain"
                      icon="mdi-eye"
                      title="View"
                      :to="{
                        name: 'Portfolio',
                        params: { addresses: element.address },
                      }"
                    />
                    <v-btn
                      size="small"
                      variant="plain"
                      :icon="element.pinned ? 'mdi-pin' : 'mdi-pin-outline'"
                      :title="
                        element.pinned
                          ? 'Remove from Quick Access'
                          : 'Add to Quick Access'
                      "
                      @click="pin(element)"
                    />
                    <v-btn
                      v-bind="props"
                      size="small"
                      variant="plain"
                      icon="mdi-dots-vertical"
                    />
                  </template>
                  <v-card min-width="200px">
                    <v-list density="compact">
                      <v-list-item
                        prepend-icon="mdi-pencil"
                        title="Rename"
                        @click="onItemEdit(element.address)"
                      />
                      <v-list-item
                        prepend-icon="mdi-share"
                        title="Share"
                        @click="share(element)"
                      />
                      <v-divider></v-divider>
                      <v-list-item
                        prepend-icon="mdi-delete"
                        title="Delete"
                        @click="removeAddress(element.address)"
                      />
                    </v-list>
                  </v-card>
                </v-menu>
              </template>
            </v-list-item>
          </template>
        </Sortable>
      </v-list>
    </v-card>
    <div
      v-if="mode === 'editBundle'"
      class="text-medium-emphasis text-right text-caption"
    >
      {{ items.length }} / {{ BUNDLE_MAX_SIZE }}
    </div>
  </div>
</template>
